import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Form, Col, Row } from "react-bootstrap";
import { PhoneInput } from "react-international-phone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Address from "../../../layout/address";
import { deepEqual, toDateOrNull } from "../../../utils/object";
import { ConnectedCourtList } from "../../../layout/select/court-list";

const mapPropsToState = (props) => ({
  courtUuid: props.courtUuid ?? "",
  title: props.title ?? "",
  appointmentDate: toDateOrNull(props.appointmentDate),
  termStart: toDateOrNull(props.termStart),
  termEnd: toDateOrNull(props.termEnd),
  ChambersAddress:
    props.ChambersAddress && typeof props.ChambersAddress === "object"
      ? { ...props.ChambersAddress }
      : {},
  chambersPhone: props.chambersPhone ?? "",
  chambersFax: props.chambersFax ?? "",
});

const Judge = forwardRef(function Judge(props, ref) {
  const defaultCountry = "us";

  const mappedProps = useMemo(
    () => mapPropsToState(props),
    [
      props.courtUuid,
      props.title,
      props.appointmentDate,
      props.termStart,
      props.termEnd,
      props.ChambersAddress,
      props.chambersPhone,
      props.chambersFax,
    ]
  );

  const [judge, setJudge] = useState(mappedProps);

  const isHydratingRef = useRef(true);
  const prevMappedRef = useRef(mappedProps);
  const initialValueRef = useRef(mappedProps);

  useEffect(() => {
    isHydratingRef.current = false;
  }, []);

  useEffect(() => {
    if (deepEqual(prevMappedRef.current, mappedProps)) return;

    isHydratingRef.current = true;
    setJudge(mappedProps);
    prevMappedRef.current = mappedProps;
    initialValueRef.current = mappedProps;

    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
  }, [mappedProps]);

  const updateField = useCallback((name, value) => {
    if (!name) return;
    setJudge((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const onChange = useCallback(
    (e) => {
      const name = e?.target?.name;
      const value = e?.target?.value;
      updateField(name, value);
    },
    [updateField]
  );

  const onAddressChange = useCallback(
    (objectName, values) => {
      updateField(objectName, { ...(values || {}) });
    },
    [updateField]
  );

  const validate = useCallback(() => {
    const errors = {};

    if (!String(judge.courtUuid ?? "").trim()) {
      errors.courtUuid = "Primary court is required.";
    }

    if (!String(judge.title ?? "").trim()) {
      errors.title = "Title is required.";
    }

    if (judge.termStart && judge.termEnd && judge.termEnd < judge.termStart) {
      errors.termEnd = "Term end cannot be before term start.";
    }

    return {
      success: Object.keys(errors).length === 0,
      errors,
    };
  }, [judge]);

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => ({
        ...judge,
        ChambersAddress: { ...(judge.ChambersAddress || {}) },
      }),
      setValue: (value = {}) => {
        const next = mapPropsToState(value);
        setJudge(next);
      },
      reset: (value = null) => {
        const next = value ? mapPropsToState(value) : initialValueRef.current;
        setJudge(next);
      },
      validate,
      isDirty: () => !deepEqual(initialValueRef.current, judge),
    }),
    [judge, validate]
  );

  return (
    <>
      <Col xs="12">
        <Row>
          <Col lg="12">
            <Row className="g-3">
              <Col lg="12">
                <h2 className="small-title">About the Bench</h2>
              </Col>
            </Row>

            <Row className="g-3">
              <Col>
                <div className="mb-3 top-label">
                  <ConnectedCourtList
                    name="courtUuid"
                    value={judge.courtUuid}
                    onChange={onChange}
                  />
                  <Form.Label>PRIMARY COURT</Form.Label>
                </div>
              </Col>
            </Row>

            <Row className="g-3">
              <Col md="8" lg="9">
                <div className="mb-3 top-label">
                  <Form.Control
                    type="text"
                    name="title"
                    value={judge.title}
                    onChange={onChange}
                  />
                  <Form.Label>TITLE</Form.Label>
                </div>
              </Col>

              <Col md="4" lg="3">
                <div className="mb-3 top-label">
                  <DatePicker
                    isClearable
                    id="appointmentDate"
                    name="appointmentDate"
                    className="form-control"
                    selected={judge.appointmentDate}
                    onChange={(date) => updateField("appointmentDate", date)}
                  />
                  <Form.Label>APPOINTED</Form.Label>
                </div>
              </Col>
            </Row>

            <Row className="g-3">
              <Col md="6">
                <div className="mb-3 top-label">
                  <DatePicker
                    isClearable
                    id="termStart"
                    name="termStart"
                    className="form-control"
                    selected={judge.termStart}
                    onChange={(date) => updateField("termStart", date)}
                  />
                  <Form.Label>TERM START</Form.Label>
                </div>
              </Col>

              <Col md="6">
                <div className="mb-3 top-label">
                  <DatePicker
                    isClearable
                    id="termEnd"
                    name="termEnd"
                    className="form-control"
                    selected={judge.termEnd}
                    onChange={(date) => updateField("termEnd", date)}
                  />
                  <Form.Label>END</Form.Label>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg="12">
            <h2 className="small-title">Chambers</h2>
          </Col>
        </Row>

        <Address
          {...judge?.ChambersAddress}
          onChange={(values) => onAddressChange("ChambersAddress", values)}
        />

        <Row className="g-3">
          <Col md="6" lg="4" xxl="3">
            <h2 className="small-title">Phone #</h2>
            <div className="mb-3">
              <PhoneInput
                name="chambersPhone"
                id="chambersPhone"
                defaultCountry={defaultCountry}
                value={judge.chambersPhone || ""}
                onChange={(value) => updateField("chambersPhone", value)}
              />
            </div>
          </Col>

          <Col md="6" lg="4" xxl="3">
            <h2 className="small-title">Fax #</h2>
            <div className="mb-3 top-label">
              <PhoneInput
                name="chambersFax"
                id="chambersFax"
                defaultCountry={defaultCountry}
                value={judge.chambersFax || ""}
                onChange={(value) => updateField("chambersFax", value)}
              />
            </div>
          </Col>
        </Row>
      </Col>
    </>
  );
});

export default Judge;